import { useState } from 'react';
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";

interface ServiceCardProps {
  title: string;
  description: string;
  fullContent: string;
  color: "primary" | "secondary" | "accent";
}

function ServiceCard({ title, description, fullContent, color }: ServiceCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const colorClasses = {
    primary: "border-primary bg-primary/5",
    secondary: "border-secondary bg-secondary/5",
    accent: "border-accent bg-accent/5"
  };
  
  const borderColorClasses = {
    primary: "border-primary",
    secondary: "border-secondary",
    accent: "border-accent"
  };

  return (
    <Card className={`border-2 ${colorClasses[color]} transition-all`}>
      <CardContent className="p-6">
        <h3 className={`text-xl font-semibold mb-3 text-${color}`}>
          {title}
        </h3>
        <p className="text-muted-foreground mb-4">
          {description}
        </p>
        
        {isExpanded && (
          <>
            <div className={`${borderColorClasses[color]} border-t my-4`} />
            <div className="mb-4 text-muted-foreground space-y-3">
              {fullContent.split('\n\n').map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
            </div>
          </>
        )}
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full"
        >
          {isExpanded ? (
            <>
              Ver menos <ChevronUp className="ml-2 h-4 w-4" />
            </>
          ) : (
            <>
              Saiba mais <ChevronDown className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

export default function Servicos() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1">
        {/* Hero */}
        <section className="relative text-primary-foreground h-[265px] flex items-center" style={{backgroundImage: "url('/images/pages/hero-amarelo.webp')", backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: "0 0 96px 0", overflow: "hidden", boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)"}}>
          <div className="absolute inset-0" style={{background: "linear-gradient(to bottom, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.6) 40%, rgba(0, 0, 0, 0.2) 70%, rgba(0, 0, 0, 0) 100%)"}}></div>
          <div className="container relative z-10">
            <h1 className="text-5xl md:text-7xl font-serif text-center" style={{fontFamily: "'Suez One', serif"}}>
              Serviços
            </h1>
          </div>
        </section>

        {/* Como trabalhamos */}
        <section className="py-16 md:py-24 bg-background">
          <div className="container">
            <h2 className="text-5xl md:text-6xl mb-12 text-primary text-center" style={{fontFamily: "'Suez One', serif"}}>
              Como nós trabalhamos?
            </h2>
            
            {/* Grid com texto e imagens alternadas */}
            <div className="space-y-16">
              {/* Bloco 1: Texto + Imagem */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div className="space-y-4 text-base md:text-lg text-muted-foreground" style={{lineHeight: "1.6"}}>
                  <p>
                    Na <strong className="text-foreground">Fercci</strong>, acreditamos que cada lavoura é única 
                    e merece uma abordagem personalizada. Por isso, oferecemos um portfólio completo de serviços 
                    que vão muito além da simples venda de fertilizantes.
                  </p>
                  <p>
                    Nossa equipe de especialistas trabalha lado a lado com você, desde o diagnóstico inicial do solo 
                    até o acompanhamento pós-aplicação, garantindo que cada decisão seja baseada em dados precisos 
                    e tecnologia de ponta.
                  </p>
                </div>
                <div className="rounded-lg overflow-hidden shadow-lg">
                  <img loading="lazy" src="/images/servicos/servicos-imagem-5.webp" alt="Equipe em campo" className="w-full h-auto object-cover" />
                </div>
              </div>
              
              {/* Bloco 2: Imagem + Texto */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div className="rounded-lg overflow-hidden shadow-lg order-2 md:order-1">
                  <img loading="lazy" src="/images/servicos/servicos-imagem-1.webp" alt="Análise de sementes" className="w-full h-auto object-cover" />
                </div>
                <div className="space-y-4 text-base md:text-lg text-muted-foreground order-1 md:order-2" style={{lineHeight: "1.6"}}>
                  <h3 className="text-2xl font-semibold text-primary">Análise e Diagnóstico</h3>
                  <p>
                    Realizamos análises completas do solo e das necessidades nutricionais da sua lavoura. Utilizamos tecnologia de ponta para identificar deficiências e oportunidades de melhoria.
                  </p>
                </div>
              </div>
              
              {/* Bloco 3: Texto + Imagem */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div className="space-y-4 text-base md:text-lg text-muted-foreground" style={{lineHeight: "1.6"}}>
                  <h3 className="text-2xl font-semibold text-primary">Aplicação e Acompanhamento</h3>
                  <p>
                    Com base no diagnóstico, recomendamos os melhores fertilizantes e técnicas de aplicação. Acompanhamos o desenvolvimento da sua lavoura durante todo o ciclo produtivo.
                  </p>
                </div>
                <div className="rounded-lg overflow-hidden shadow-lg">
                  <img loading="lazy" src="/images/servicos/servicos-imagem-2.webp" alt="Profissional com produto" className="w-full h-auto object-cover" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Nossos Serviços */}
        <section className="py-16 md:py-24" style={{backgroundColor: "#F2F2F2"}}>
          <div className="container">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ServiceCard
                title="Consultoria Personalizada em Fertilizantes"
                description="Análise completa e recomendações técnicas sob medida para sua propriedade."
                fullContent="Nossa consultoria começa com uma análise detalhada do solo e das necessidades específicas da sua cultura. Nossos engenheiros agrônomos avaliam fatores como histórico da área, clima, tipo de solo e objetivos de produção para criar um plano nutricional personalizado.

Oferecemos visitas técnicas regulares, acompanhamento de safra e ajustes nas recomendações conforme necessário. Nosso objetivo é maximizar a eficiência do uso de fertilizantes, reduzindo custos e aumentando a produtividade."
                color="primary"
              />

              <ServiceCard
                title="Tecnologia a Serviço da Agricultura"
                description="Ferramentas digitais e agricultura de precisão para otimizar seus resultados."
                fullContent="Utilizamos as mais avançadas tecnologias de agricultura de precisão, incluindo a plataforma Atfarm da Yara, que permite o monitoramento em tempo real da sua lavoura via satélite.

Com mapas de variabilidade, análise de biomassa e recomendações de aplicação variável, você pode aplicar fertilizantes de forma precisa, economizando insumos e aumentando a produtividade. Além disso, oferecemos treinamento completo para que você aproveite ao máximo essas ferramentas."
                color="secondary"
              />

              <ServiceCard
                title="Fertilizantes Premium"
                description="Produtos de alta qualidade da Yara e outras marcas líderes mundiais."
                fullContent="Como representantes oficiais da Yara no Cerrado, oferecemos acesso exclusivo aos fertilizantes mais avançados do mercado. Nossa linha inclui produtos especiais como YaraMila, YaraBasa, YaraBela e YaraVita, formulados para atender às necessidades específicas de cada cultura.

Trabalhamos com pedidos mínimos de 37 toneladas, garantindo preços competitivos e entrega direta da fábrica para sua propriedade. Todos os produtos são acompanhados de certificados de qualidade e suporte técnico completo."
                color="accent"
              />

              <ServiceCard
                title="Rede de Vantagens"
                description="Programa de fidelidade exclusivo com benefícios e recompensas."
                fullContent="Nosso programa Boa Colheita Produtores, em parceria com a Orbia, recompensa sua fidelidade com pontos que podem ser trocados por produtos e serviços exclusivos.

A cada compra, você acumula pontos de acordo com a categoria do produto. Quanto mais você compra, mais benefícios recebe. É nossa forma de agradecer pela confiança e parceria de longo prazo."
                color="primary"
              />

              <ServiceCard
                title="Assistência Técnica"
                description="Suporte contínuo durante todo o ciclo produtivo."
                fullContent="Nossa equipe de assistência técnica está sempre disponível para atender suas dúvidas e necessidades. Oferecemos suporte via telefone, WhatsApp e visitas presenciais quando necessário.

Acompanhamos o desenvolvimento da sua lavoura, identificamos possíveis deficiências nutricionais e ajustamos as recomendações em tempo real. Nosso compromisso é garantir que você tenha todo o suporte necessário para alcançar os melhores resultados."
                color="secondary"
              />

              <ServiceCard
                title="Treinamento e Capacitação"
                description="Eventos, palestras e workshops para manter você atualizado."
                fullContent="Organizamos eventos exclusivos como a Safra Financeira e participamos das principais feiras do agronegócio, como a AgroBrasília. Nesses eventos, você tem acesso a palestras com especialistas, demonstrações de produtos e oportunidades de networking.

Além disso, oferecemos treinamentos específicos sobre o uso de tecnologias, manejo de fertilizantes e tendências do mercado. Nosso objetivo é que você esteja sempre à frente, tomando decisões informadas e estratégicas."
                color="accent"
              />
            </div>
          </div>
        </section>

        {/* Galeria de Imagens */}
        <section className="py-16 md:py-24" style={{backgroundColor: "#F2F2F2"}}>
          <div className="container">
            <h2 className="text-5xl md:text-6xl mb-12 text-primary text-center" style={{fontFamily: "'Suez One', serif"}}>
              Nosso Trabalho em Ação
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img loading="lazy" src="/images/servicos/servicos-imagem-3.webp" alt="Equipe em armazém" className="w-full h-auto object-cover" />
              </div>
              <div className="rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img loading="lazy" src="/images/servicos/servicos-imagem-4.webp" alt="Equipe em campo" className="w-full h-auto object-cover" />
              </div>
            </div>
          </div>
        </section>

        {/* Solicite um Orçamento */}
        <section className="py-16 md:py-24 bg-background">
          <div className="container text-center">
            <h2 className="text-5xl md:text-6xl mb-6 text-primary" style={{fontFamily: "'Suez One', serif"}}>
              Solicite um orçamento
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Entre em contato conosco para receber uma proposta personalizada para sua propriedade.
            </p>
            <Button 
              size="lg" 
              className="bg-accent hover:bg-accent/90 text-primary text-base font-semibold px-8 py-6"
              onClick={() => window.location.href = '/contato'}
            >
              SOLICITAR ORÇAMENTO
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

